// src/pages/FirmProfilePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  Upload,
  Avatar,
  Typography,
  Row,
  Col,
  Button,
  Space,
  message,
  Skeleton,
} from "antd";
import { UserOutlined, UploadOutlined } from "@ant-design/icons";
import {
  fetchCountries,
  fetchStates,
  fetchCities,
  fetchFirms,
  updateFirm,
} from "../../../api/firmApi"; // <-- adjust path if needed
import { withBase } from "../../../config/env";


const { Title, Text } = Typography;

// ---------- Helpers ----------
function safeGetLoggedInUserId() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.id ?? user?.user_id ?? user?.data?.id ?? user?.data?.user_id ?? null;
  } catch {
    return null;
  }
}
const buildImageUrl = (p) => {
  if (!p) return null;
  if (/^https?:\/\//i.test(p)) return p;
  const clean = String(p).replace(/^\/+/, "").replace(/\\/g, "/");
  return withBase(clean);
};

export default function FirmProfilePage() {
  const [form] = Form.useForm();

  const userId = useMemo(safeGetLoggedInUserId, []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [firm, setFirm] = useState(null);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // New upload preview + list
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [dbImgErrored, setDbImgErrored] = useState(false);

  // ------- Initial data load -------
  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!userId) {
        message.error("User not found in local storage. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const [countriesRes] = await Promise.all([fetchCountries()]);
        if (!mounted) return;
        setCountries(countriesRes || []);

        const list = await fetchFirms();
        if (!mounted) return;

        // find firm by user_id first, fallback by id
        const found =
          list.find((f) => String(f.user_id) === String(userId)) ||
          list.find((f) => String(f.id) === String(userId)) ||
          null;

        if (!found) {
          message.error("Firm profile not found.");
          setLoading(false);
          return;
        }

        setFirm(found);

        // cascades
        if (found.country_id) {
          const stateList = await fetchStates(found.country_id);
          if (!mounted) return;
          setStates(stateList);
        }
        if (found.state_id) {
          const cityList = await fetchCities(found.state_id);
          if (!mounted) return;
          setCities(cityList);
        }

        form.setFieldsValue({
          name: found.name,
          email: found.email,
          contact_number: found.contact_number,
          address: found.address,
          country_id: found.country_id,
          state_id: found.state_id,
          city_id: found.city_id,
          status: found.status || "Active",
        });

        setDbImgErrored(false);
      } catch (e) {
        console.error(e);
        message.error("Failed to load firm profile.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [userId, form]);

  // ------- Dropdown change handlers -------
  const onCountryChange = async (countryId) => {
    form.setFieldsValue({ state_id: undefined, city_id: undefined });
    setStates([]);
    setCities([]);
    if (!countryId) return;
    try {
      const stateList = await fetchStates(countryId);
      setStates(stateList);
    } catch (e) {
      console.error(e);
      message.error("Failed to load states.");
    }
  };

  const onStateChange = async (stateId) => {
    form.setFieldsValue({ city_id: undefined });
    setCities([]);
    if (!stateId) return;
    try {
      const cityList = await fetchCities(stateId);
      setCities(cityList);
    } catch (e) {
      console.error(e);
      message.error("Failed to load cities.");
    }
  };

  // ------- Image upload/preview -------
  const beforeUpload = (file) => {
    const isImage =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp" ||
      file.type === "image/jpg";
    if (!isImage) {
      message.error("Only JPG/PNG/WEBP files are allowed.");
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB.");
      return Upload.LIST_IGNORE;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setLogoPreview(dataUrl);
      setFileList([
        {
          uid: String(Date.now()),
          name: file.name || "logo",
          status: "done",
          url: dataUrl,
          thumbUrl: dataUrl,
        },
      ]);
    };
    reader.readAsDataURL(file);

    setLogoFile(file);
    return false;
  };

  // ------- Submit -------
  const onFinish = async (values) => {
    if (!userId) {
      message.error("User not found. Please log in again.");
      return;
    }

    try {
      setSaving(true);

      const formData = new FormData();
      const appendIfPresent = (k, v) => {
        if (v !== undefined && v !== null && v !== "") formData.append(k, v);
      };

      appendIfPresent("name", values.name);
      appendIfPresent("email", values.email);
      appendIfPresent("contact_number", values.contact_number);
      appendIfPresent("address", values.address);
      appendIfPresent("country_id", values.country_id);
      appendIfPresent("state_id", values.state_id);
      appendIfPresent("city_id", values.city_id);
      appendIfPresent("status", values.status);

      if (logoFile) formData.append("image", logoFile);

      const apiRes = await updateFirm(userId, formData);
      const payload = apiRes?.data ?? apiRes; // normalize axios vs direct

      if (payload?.status_code === 200) {
        const oldUser = JSON.parse(localStorage.getItem("user")) || {};
        const { id, name, email, role, status, image } = payload.data || {}; // backend should return these

        // Update localStorage (keep token)
        localStorage.setItem(
          "user",
          JSON.stringify({
            id,
            name,
            email,
            role,
            status,
            image,
            token: oldUser.token || null,
          })
        );

        // reflect changes locally
        setFirm((prev) => ({
          ...(prev || {}),
          ...values,
          image: image ?? (prev ? prev.image : null),
        }));

        message.success(payload.message || "Firm updated successfully.");
      } else {
        message.error(payload?.message || "Failed to update firm.");
      }
    } catch (e) {
      console.error(e);
      message.error("Failed to update firm.");
    } finally {
      setSaving(false);
    }
  };

  const dbImage = firm?.image ? `${buildImageUrl(firm.image)}?v=${firm?.updated_at || ""}` : null;

  // ------- UI -------
  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: 24 }}>
      <Card
        style={{
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
        }}
        bodyStyle={{ padding: 0 }}
      >
        {/* Header */}
        <div
          style={{
            background:
              "linear-gradient(135deg, rgba(24,144,255,.12), rgba(82,196,26,.12))",
            padding: "28px",
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          {/* Left: Text */}
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {firm?.name || "Edit Firm Profile"}
            </Title>
            <Text type="secondary">
              Update firm info, address and logo.
            </Text>
          </div>

          {/* Right: Avatars */}
          <div style={{ marginLeft: "auto" }}>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              {/* Current (DB) Avatar */}
              <div style={{ textAlign: "center" }}>
                <Avatar
                  size={80}
                  src={!dbImgErrored ? dbImage : null}
                  onError={() => {
                    setDbImgErrored(true);
                    return false;
                  }}
                  icon={dbImgErrored && <UserOutlined />}
                  style={{ border: "2px solid rgba(0,0,0,.07)" }}
                />
                <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
                  Current
                </div>
              </div>

              {/* New (Preview) Avatar */}
              {logoPreview && (
                <div style={{ textAlign: "center" }}>
                  <Avatar
                    size={80}
                    src={logoPreview}
                    style={{ border: "2px solid rgba(24,144,255,.35)" }}
                  />
                  <div style={{ fontSize: 12, color: "#1890ff", marginTop: 6 }}>
                    New
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ padding: 24 }}>
          {loading ? (
            <Skeleton active paragraph={{ rows: 8 }} />
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{ status: "Active" }}
            >
              {/* Logo Upload */}
              <Card size="small" style={{ borderRadius: 12, marginBottom: 16 }}>
                <Title level={5} style={{ marginTop: 0 }}>
                  Firm Logo
                </Title>

                <Upload
                  accept="image/*"
                  listType="picture-card"
                  showUploadList
                  beforeUpload={beforeUpload}
                  fileList={fileList}
                  maxCount={1}
                  onRemove={() => {
                    setLogoPreview(null);
                    setLogoFile(null);
                    setFileList([]);
                  }}
                >
                  {fileList.length === 0 && (
                    <div style={{ padding: 8 }}>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>Upload</div>
                    </div>
                  )}
                </Upload>

                <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                  JPG/PNG/WEBP up to 5MB.
                </Text>
              </Card>

              {/* Firm Info */}
              <Card size="small" style={{ borderRadius: 12, marginBottom: 16 }}>
                <Title level={5} style={{ marginTop: 0 }}>
                  Firm Information
                </Title>
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Firm Name"
                      name="name"
                      rules={[{ required: true, message: "Please enter firm name" }]}
                    >
                      <Input placeholder="Enter firm name" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Email"
                      name="email"
                      rules={[{ type: "email", message: "Invalid email" }]}
                    >
                      <Input placeholder="firm@email.com" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="Contact Number" name="contact_number">
                      <Input placeholder="+92 3xx xxxxxxx" />
                    </Form.Item>
                  </Col>
                  <Col  xs={24} md={12}>
                    <Form.Item label="Status" name="status" hidden>
                      <Select
                        options={[
                          { value: "Active", label: "Active" },
                          { value: "Inactive", label: "Inactive" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item label="Address" name="address">
                      <Input.TextArea rows={3} placeholder="Street, area, etc." />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Address */}
              <Card size="small" style={{ borderRadius: 12, marginBottom: 16 }}>
                <Title level={5} style={{ marginTop: 0 }}>
                  Address Details
                </Title>
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item label="Country" name="country_id">
                      <Select
                        showSearch
                        placeholder="Select country"
                        optionFilterProp="label"
                        onChange={onCountryChange}
                        options={(countries || []).map((c) => ({
                          value: c.id,
                          label: c.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="State" name="state_id">
                      <Select
                        showSearch
                        placeholder="Select state"
                        optionFilterProp="label"
                        onChange={onStateChange}
                        options={(states || []).map((s) => ({
                          value: s.id,
                          label: s.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item label="City" name="city_id">
                      <Select
                        showSearch
                        placeholder="Select city"
                        optionFilterProp="label"
                        options={(cities || []).map((c) => ({
                          value: c.id,
                          label: c.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <Space style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={() => {
                    form.resetFields();
                    setLogoPreview(null);
                    setLogoFile(null);
                    setFileList([]);
                    setDbImgErrored(false);
                  }}
                >
                  Reset
                </Button>
                <Button type="primary" htmlType="submit" loading={saving}>
                  Save Changes
                </Button>
              </Space>
            </Form>
          )}
        </div>
      </Card>
    </div>
  );
}
